import type { ListPlaybooksWithAnalyticsResponse } from '~/api/atlas/typedefs.autogen';
import type { LastModifiedBucket } from '~/features/playbooks/playbookLastModified';
import type { PlaybooksTableSort } from '~/features/playbooks/playbooksTableDefaults';
import type { PlaybookTableRow } from '~/features/playbooks/types/PlaybookTableRow';
import type {
  ListPlaybooksResponse,
  PlaybookStatus,
} from '~/redux/api/types/playbooks/playbooks';

type PlaybookListField =
  | 'name'
  | 'status'
  | 'guidelines'
  | 'created_by_pilot_user_id'
  | 'updated_by_pilot_user_id'
  | 'created_at'
  | 'updated_at'
  | 'analytics';

export type PlaybookListArg = {
  lastModified?: LastModifiedBucket | null;
  pageIndex: number;
  pageSize: number;
  search?: string;
  sortBy?: PlaybooksTableSort;
  status?: PlaybookStatus | null;
  workflowId?: string | null;
  fields?: PlaybookListField[];
};

// Maps the table's react-table column ids to the `sort` values the endpoint
// validates against (`ListPlaybooksSortField` in atlas). `NO_PLAYBOOKS_SORT`
// (id `''`) has no entry, so its lookup is `undefined` and the param is
// omitted — the server already defaults to `name` ascending.
const SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  name: 'name',
  status: 'status',
  completedReviewsCount: 'total_completed_reviews_count',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  lastUsedAt: 'last_run_at',
};

const mapSort = (sortBy?: PlaybooksTableSort): string | undefined => {
  if (!sortBy) {
    return undefined;
  }
  const field = SORT_FIELD_BY_COLUMN_ID[sortBy.id];
  return field && sortBy.desc ? `-${field}` : field;
};

export const mapPlaybookListParams = (
  arg: PlaybookListArg,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    'page[offset]': (arg.pageIndex - 1) * arg.pageSize,
    'page[limit]': arg.pageSize,
  };

  const sort = mapSort(arg.sortBy);
  if (sort) {
    params.sort = sort;
  }
  if (arg.search) {
    params.name = arg.search;
  }
  if (arg.lastModified) {
    params.last_modified = arg.lastModified;
  }
  if (arg.status) {
    params.status = arg.status;
  }
  if (arg.workflowId) {
    params.workflow_id = arg.workflowId;
  }
  if (arg.fields?.length) {
    params.fields = arg.fields.join(',');
  }

  return params;
};

const toTableRow = (
  resource: ListPlaybooksWithAnalyticsResponse['data'][number],
): PlaybookTableRow => {
  const playbook = resource.attributes;
  return {
    id: resource.id,
    name: playbook.name ?? '',
    status: playbook.status,
    createdAt: playbook.created_at ?? null,
    createdByPilotUserId: playbook.created_by_pilot_user_id ?? null,
    updatedAt: playbook.updated_at ?? null,
    updatedByPilotUserId: playbook.updated_by_pilot_user_id ?? null,
    completedReviewsCount:
      playbook.analytics?.total_completed_reviews_count ?? 0,
    lastUsedAt: playbook.analytics?.last_run_at ?? null,
  };
};

export const mapPlaybookListResponse = (
  response: ListPlaybooksWithAnalyticsResponse,
): { rows: PlaybookTableRow[]; totalCount: number } => ({
  rows: response.data.map(toTableRow),
  totalCount: response.meta.page?.total ?? 0,
});

export type PlaybookListSummary = {
  id: string;
  name: string;
  status?: PlaybookStatus;
  guidelines?: ListPlaybooksWithAnalyticsResponse['data'][number]['attributes']['guidelines'];
};

export const mapPlaybookListSummaries = (
  response: ListPlaybooksWithAnalyticsResponse,
): { items: PlaybookListSummary[]; totalCount: number } => ({
  items: response.data.map((resource) => {
    const playbook = resource.attributes;
    return {
      id: resource.id,
      name: playbook.name ?? '',
      status: playbook.status,
      guidelines: playbook.guidelines,
    };
  }),
  totalCount: response.meta.page?.total ?? 0,
});

/**
 * Maps `/playbooks/list` into the legacy `getPlaybooks` `{ data }` envelope.
 *
 * `getPlaybooks` only requests the `name` field (see `enhancements.ts`), so
 * only `uuid`/`name`/`description` (the fields required by `PlaybookSummary`)
 * are mapped here. The other `PlaybookSummary` fields are optional and are
 * intentionally left unset rather than fabricated from an unrequested,
 * server-defaulted value.
 */
export const mapPlaybookListToLegacyData = (
  response: ListPlaybooksWithAnalyticsResponse,
): Pick<ListPlaybooksResponse, 'data'> => ({
  data: response.data.map(({ attributes, id, type }) => ({
    id,
    type,
    attributes: {
      uuid: attributes.uuid,
      name: attributes.name ?? '',
      description: attributes.description ?? '',
    },
  })),
});
